/**
 * Per-turn agentic budget for Meridian: how many tool-call STEPS a turn may take,
 * and how long it may take in WALL-CLOCK time.
 *
 * The two are deliberately paired. Raising the step cap on its own converts a
 * "stopped early" failure into a "killed by the platform mid-stream" failure,
 * which is strictly worse: on a serverless timeout the response dies in flight,
 * `onFinish` never runs, and the turn is not persisted at all. So the loop is
 * bounded by whichever comes first — the step cap or the deadline — and both
 * stops are CLEAN (the SDK finishes the current step and emits a final message).
 *
 * Resolve ONCE per request and pass the result down, so a mid-request env change
 * cannot produce a split-brain budget (same discipline as
 * `resolveContextWindowConfig`).
 */

import { stepCountIs, type StopCondition, type ToolSet } from 'ai';
import { DEFAULT_STEP_COUNT_LIMIT, DEFAULT_TURN_DEADLINE_MS } from './constants';

/** Resolved per-turn agentic budget. */
export interface StepBudget {
  /** Max model requests (steps) in the tool loop. */
  maxSteps: number;
  /** Wall-clock budget for the whole turn, in milliseconds. */
  deadlineMs: number;
}

/**
 * Lower bound for the deadline. A budget below this cannot complete even a
 * trivial single-step turn, so a typo'd env var would make every turn a no-op.
 */
const MIN_DEADLINE_MS = 10_000;

/** Parse a positive-integer env override; garbage/missing values fall back. */
function readPositiveIntEnv(name: string, fallback: number): number {
  const raw = process.env[name]?.trim();
  if (!raw) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Resolve the per-turn budget.
 *
 * - `MERIDIAN_STEP_COUNT_LIMIT` (default {@link DEFAULT_STEP_COUNT_LIMIT})
 * - `MERIDIAN_TURN_DEADLINE_MS` (default {@link DEFAULT_TURN_DEADLINE_MS},
 *   floored at {@link MIN_DEADLINE_MS})
 *
 * Background (queue-driven) turns are not bound by a serverless request timeout,
 * so their caller passes a larger `deadlineMs` explicitly via `overrides`.
 */
export function resolveStepBudget(overrides?: Partial<StepBudget>): StepBudget {
  const maxSteps =
    overrides?.maxSteps ??
    readPositiveIntEnv('MERIDIAN_STEP_COUNT_LIMIT', DEFAULT_STEP_COUNT_LIMIT);

  const rawDeadline =
    overrides?.deadlineMs ??
    readPositiveIntEnv('MERIDIAN_TURN_DEADLINE_MS', DEFAULT_TURN_DEADLINE_MS);

  return {
    maxSteps,
    deadlineMs: Math.max(MIN_DEADLINE_MS, rawDeadline),
  };
}

/**
 * A {@link StopCondition} that ends the tool loop once `deadlineMs` has elapsed
 * since the condition was created.
 *
 * The clock starts when this function is called — do that immediately before
 * handing the condition to `streamText`, so the budget covers the turn and not
 * the setup that preceded it.
 *
 * Stop conditions are evaluated BETWEEN steps, so this bounds when a NEW step may
 * start; it never interrupts a step already in flight. That is the intended
 * behavior — the headroom under the function timeout absorbs the final step.
 */
export function deadlineReached<TOOLS extends ToolSet>(
  deadlineMs: number,
): StopCondition<TOOLS> {
  const startedAt = Date.now();
  return () => Date.now() - startedAt >= deadlineMs;
}

/**
 * Build the `stopWhen` array for a turn: stop at the step cap OR the deadline,
 * whichever comes first.
 */
export function buildStopConditions<TOOLS extends ToolSet>(
  budget: StepBudget,
): Array<StopCondition<TOOLS>> {
  return [stepCountIs(budget.maxSteps), deadlineReached<TOOLS>(budget.deadlineMs)];
}
